import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { QuickSendContext } from "contexts/QuickSend";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { BentoBrand, UpgradeDialogSource } from "schemas/dashboard";

import { AI_DRAFT_CREDITS_TOOLTIP } from "constants/influencer";
import { trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import IncludeOptions from "./IncludeOptions";
import LoadingView from "./LoadingView";
import ToneSelector from "./ToneSelector";
import {
  FormState,
  ValidationKey,
  buildPayload,
  initialFormState,
  stepsForForm,
  validateForm,
} from "./schema";
import { useStreamingPersonalize } from "./useStreamingPersonalize";

interface Props {
  open: boolean;
  onClose: () => void;
  bentoBrand: BentoBrand;
  currentDraftHtml: string;
  onApply: (html: string) => void;
}

type View = "form" | "loading";

export default function PersonalizeDialog({
  open,
  onClose,
  bentoBrand,
  currentDraftHtml,
  onApply,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { contactName } = useContext(QuickSendContext);
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);
  const { decreaseChatCount } = useSubscription();
  const [form, setForm] = useState<FormState>(initialFormState);
  const [view, setView] = useState<View>("form");
  const [errorKey, setErrorKey] = useState<ValidationKey>(null);
  const stream = useStreamingPersonalize();

  const remainingDrafts = subscription?.remainingChatMessages ?? 0;
  const outOfDrafts = remainingDrafts <= 0;

  // Clear the error highlight as soon as the user touches the form.
  useEffect(() => {
    if (errorKey) setErrorKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const handleClose = () => {
    stream.cancel();
    setView("form");
    setForm(initialFormState);
    setErrorKey(null);
    stream.reset();
    onClose();
  };

  const handleSubmit = () => {
    if (outOfDrafts) {
      setUpgradeDialogSource(
        UpgradeDialogSource.hits_personalized_ai_messages_limit,
      );
      return;
    }

    const invalid = validateForm(form);
    if (invalid) {
      setErrorKey(invalid);
      return;
    }

    const payload = buildPayload(
      form,
      bentoBrand.id,
      currentDraftHtml,
      contactName ?? undefined,
      "personalize_dialog",
    );
    const steps = stepsForForm(form);

    trackEvent("Personalize Dialog Rewrite Draft Button Pressed", {
      "Brand ID": bentoBrand.id,
      "Reference Instagram": form.referenceInstagram,
      "Reference Product": form.referenceProduct,
      "Mention Case Study": form.mentionCaseStudy,
      "Content Idea": form.contentIdea,
      "Audience Alignment": form.audienceAlignment,
      Tone: form.tone,
      "Has Freeform": (form.freeform ?? "").trim().length > 0,
    });

    decreaseChatCount();

    setView("loading");
    stream.run(payload, steps);
  };

  const handleApply = () => {
    const html = stream.finalHtml ?? "";
    if (!html) return;
    trackEvent("Personalize Dialog Use Draft Button Pressed", {
      "Brand ID": bentoBrand.id,
    });
    onApply(html);
    handleClose();
  };

  const handleTryAgain = () => {
    stream.reset();
    trackEvent("Personalize Dialog Try Again Pressed", {
      "Brand ID": bentoBrand.id,
    });
    setView("form");
  };

  const draftDone = stream.finalHtml !== null;
  const hasError = !!stream.errorMessage;

  return (
    <Dialog
      open={open}
      onClose={stream.running ? undefined : handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      scroll={isMobile ? "paper" : "body"}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2.5 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                component="i"
                className="fa-solid fa-wand-magic-sparkles"
                sx={{ color: "#8084FF", fontSize: 18 }}
              />
              <Typography
                sx={{ fontSize: 18, fontWeight: 600, color: "#0a0a0a" }}
              >
                Personalize This Draft
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: "#717182" }}>
              Select prompts to customize your pitch. Uses 1 credit.
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleClose}
            disabled={stream.running}
          >
            <Box component="i" className="fa-regular fa-xmark" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers={false}
        sx={{ pt: 1, overflow: isMobile ? "auto" : "visible" }}
      >
        {view === "form" ? (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Box>
              <Typography
                sx={{ fontWeight: 500, fontSize: 14, mb: 1, color: "#0a0a0a" }}
              >
                What should AI include?
              </Typography>
              <IncludeOptions
                state={form}
                setState={setForm}
                bentoBrand={bentoBrand}
                errorKey={errorKey}
              />
            </Box>

            <Box>
              <Typography
                sx={{ fontWeight: 500, fontSize: 14, mb: 1, color: "#0a0a0a" }}
              >
                Adjust tone or length?
              </Typography>
              <ToneSelector
                selected={form.tone}
                onChange={(tone) => setForm({ ...form, tone })}
              />
            </Box>

            <Box>
              <Typography
                sx={{ fontWeight: 500, fontSize: 14, mb: 1, color: "#0a0a0a" }}
              >
                Anything else?
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                size="small"
                placeholder="e.g., Focus on video content, mention TikTok experience..."
                value={form.freeform}
                onChange={(e) => setForm({ ...form, freeform: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    background: "#f3f3f5",
                    "& fieldset": { borderColor: "transparent" },
                  },
                }}
              />
            </Box>
          </Stack>
        ) : (
          <LoadingView
            steps={stream.steps}
            deltaHtml={stream.deltaHtml}
            finalHtml={stream.finalHtml}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: "1px solid rgba(0,0,0,0.1)" }}
      >
        <Typography
          variant="body2"
          component="div"
          sx={{
            color: outOfDrafts ? "#c0392b" : "#6a7282",
            flex: 1,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
          }}
        >
          {remainingDrafts} AI-generated draft
          {remainingDrafts === 1 ? "" : "s"} left
          <Tooltip
            slotProps={{
              tooltip: {
                sx: {
                  color: "black",
                  backgroundColor: "white",
                  p: 1,
                  boxShadow: 1,
                },
              },
            }}
            title={AI_DRAFT_CREDITS_TOOLTIP}
            enterTouchDelay={0}
          >
            <Box
              component="i"
              className="fa-solid fa-circle-info"
              sx={{ ml: 0.75, color: "info.main", cursor: "help" }}
            />
          </Tooltip>
        </Typography>

        {view === "form" ? (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              size="small"
              sx={{
                textTransform: "none",
                borderColor: "rgba(0,0,0,0.1)",
                color: "#0a0a0a",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              size="small"
              disableElevation
              sx={{
                textTransform: "none",
                background: "#8084FF",
                color: "white",
                "&:hover": { background: "#6970E8" },
              }}
            >
              Rewrite Draft
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              size="small"
              sx={{
                textTransform: "none",
                borderColor: "rgba(0,0,0,0.1)",
                color: "#0a0a0a",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleTryAgain}
              variant="outlined"
              size="small"
              disabled={stream.running}
              sx={{
                textTransform: "none",
                borderColor: "rgba(0,0,0,0.1)",
                color: "#0a0a0a",
              }}
            >
              Try again
            </Button>
            <Button
              onClick={handleApply}
              disabled={!draftDone || hasError}
              variant="contained"
              size="small"
              disableElevation
              sx={{
                textTransform: "none",
                background: "#8084FF",
                color: "white",
                "&:hover": { background: "#6970E8" },
                "&.Mui-disabled": { background: "#E0E1FB", color: "white" },
              }}
            >
              Use this draft
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
